// Small motion primitives shared by the landing page sections.
import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring, useReducedMotion } from 'framer-motion';
import './shared.css';

export const EASE_OUT = [0.16, 1, 0.3, 1];

// Splits a heading into words, each sliding up out of its own overflow mask.
// `lines` is an array of strings (one per visual line) or of arrays of
// { text, className } segments for highlighted words.
export function MaskedHeading({ as: Tag = 'h2', lines, className = '', delay = 0, stagger = 0.06, immediate = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -12% 0px' });
  const reduce = useReducedMotion();
  const show = immediate || inView;
  let i = 0;
  return (
    <Tag ref={ref} className={`lp-masked ${className}`}>
      {lines.map((line, li) => {
        const segments = typeof line === 'string' ? [{ text: line }] : line;
        return (
          <span className="lp-masked-line" key={li}>
            {segments.map((seg, si) =>
              seg.text.split(' ').filter(Boolean).map((word, wi) => {
                const idx = i;
                i += 1;
                return (
                  <span className="lp-masked-word" key={`${si}-${wi}`}>
                    <motion.span
                      className={seg.className}
                      initial={reduce ? false : { y: '110%', rotate: 4 }}
                      animate={show ? { y: '0%', rotate: 0 } : undefined}
                      transition={{ duration: 1.05, ease: EASE_OUT, delay: delay + idx * stagger }}
                    >
                      {word}
                    </motion.span>
                  </span>
                );
              })
            )}
          </span>
        );
      })}
    </Tag>
  );
}

export function Reveal({ children, delay = 0, y = 36, className = '', as = 'div' }) {
  const reduce = useReducedMotion();
  const Comp = motion[as];
  return (
    <Comp
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -10% 0px' }}
      transition={{ duration: 0.9, ease: EASE_OUT, delay }}
    >
      {children}
    </Comp>
  );
}

// Wraps a CTA so it leans toward the pointer while hovered.
export function Magnetic({ children, strength = 0.35, className = '' }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 16, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 16, mass: 0.4 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };
  return (
    <motion.span ref={ref} className={`lp-magnetic ${className}`} style={{ x: sx, y: sy }} onPointerMove={onMove} onPointerLeave={reset}>
      {children}
    </motion.span>
  );
}

export function Marquee({ items, reverse = false, className = '' }) {
  const row = [...items, ...items];
  return (
    <div className={`lp-marquee ${reverse ? 'is-reverse' : ''} ${className}`} aria-hidden="true">
      <div className="lp-marquee-track">
        {row.map((item, i) => (
          <span className="lp-marquee-item" key={i}>
            {item}
            <span className="lp-marquee-dot" />
          </span>
        ))}
      </div>
    </div>
  );
}

// Counts up to `value` once scrolled into view.
export function CountUp({ value, duration = 1.6, suffix = '', prefix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      setN(Math.round(value * (1 - Math.pow(1 - t, 4))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);
  return (
    <span ref={ref}>
      {prefix}
      {n.toLocaleString('tr-TR')}
      {suffix}
    </span>
  );
}

// Desktop-only trailing cursor: a dot plus a ring that swells over links and
// buttons and shows a label for elements with data-cursor="...".
export function Cursor() {
  const [enabled, setEnabled] = useState(false);
  const [state, setState] = useState({ hover: false, label: '' });
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const rx = useSpring(x, { stiffness: 380, damping: 32, mass: 0.6 });
  const ry = useSpring(y, { stiffness: 380, damping: 32, mass: 0.6 });

  useEffect(() => {
    const mq = window.matchMedia('(pointer: fine) and (prefers-reduced-motion: no-preference)');
    setEnabled(mq.matches);
    if (!mq.matches) return undefined;
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };
    const over = (e) => {
      const target = e.target.closest('a, button, [data-cursor]');
      setState({ hover: !!target, label: target?.getAttribute('data-cursor') || '' });
    };
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerover', over, { passive: true });
    document.documentElement.classList.add('lp-has-cursor');
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerover', over);
      document.documentElement.classList.remove('lp-has-cursor');
    };
  }, [x, y]);

  if (!enabled) return null;
  return (
    <>
      <motion.div className="lp-cursor-dot" style={{ x, y }} />
      <motion.div
        className={`lp-cursor-ring ${state.hover ? 'is-hover' : ''} ${state.label ? 'has-label' : ''}`}
        style={{ x: rx, y: ry }}
      >
        {state.label && <span>{state.label}</span>}
      </motion.div>
    </>
  );
}

// Hand-drawn signature flourish that draws itself once scrolled into view.
export function SignatureStroke({ className = '', delay = 0.6, duration = 2.2 }) {
  const reduce = useReducedMotion();
  return (
    <svg className={`lp-stroke ${className}`} viewBox="0 0 600 170" fill="none" aria-hidden="true">
      <motion.path
        d="M18 118 C 40 64, 72 38, 84 70 C 96 102, 62 134, 50 112 C 38 90, 92 58, 122 80 C 142 95, 132 122, 152 112 C 172 102, 178 72, 194 76 C 210 80, 194 116, 214 113 C 234 110, 244 80, 260 82 C 276 84, 266 114, 284 111 C 306 107, 316 58, 338 36 C 354 21, 362 48, 348 80 C 336 108, 330 130, 352 121 C 378 111, 396 84, 418 90 C 440 96, 428 120, 450 115 C 480 108, 516 80, 586 68"
        initial={reduce ? false : { pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration, ease: [0.65, 0, 0.35, 1], delay }}
      />
      <motion.path
        d="M64 150 C 200 132, 390 136, 566 116"
        initial={reduce ? false : { pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: duration * 0.4, ease: [0.65, 0, 0.35, 1], delay: delay + duration * 0.85 }}
      />
    </svg>
  );
}
